import { createContext, useState, useCallback, useEffect, useContext } from "react";
import TerminalService from "../services/TerminalService";
import { STORAGE_KEYS, EVENTS, SYSTEM_VIEW } from "const";
import { eventsEmitter } from "utils/events";
import * as TerminalHelper from "helpers/TermialHelper";
import ShopContext from "./ShopContext";
import toast from "react-hot-toast";
import InvoiceFormatService from "../services/InvoiceFormatService";
import * as InvoiceHelper from "helpers/InvoiceHelper";

export const TerminalContext = createContext();

export const TerminalProvider = ({ children }) => {
  const { hasFullKotValidity } = useContext(ShopContext);

  const [terminalInvoiceFormat, setTerminalInvoiceFormat] = useState(null);

  const [terminalSetting, setTerminalSettings] = useState(() => {
    const savedSettings = TerminalHelper.getTerminalSettings() || {};
    const savedToken = TerminalHelper.getLocalTokenNumber() || 1;
    return {
      selectedDashboards: [],
      loading: false,
      activeOrders: savedSettings.isOrderDashboardEnabled || false,
      tableDashboard: (savedSettings.isTableDashboardEnabled || false) && hasFullKotValidity,
      displayAwaitingDispatchOrders: savedSettings.displayAwaitingDispatchOrders ?? false,
      showTokenNumber: savedSettings.showTokenNumber ?? false,
      localTokenNumber: savedToken || 1,
      printOnPayment: savedSettings.printOnPayment || false,
      settingsCache: null,
    };
  });

  const fetchInvoiceFormat = useCallback(
    async (terminalId, force = false) => {
      if (terminalInvoiceFormat && !force) {
        return terminalInvoiceFormat;
      }
      if (!terminalId) return null;
      try {
        const response = await InvoiceFormatService.getInvoiceFormat(terminalId);
        const data = response?.data || response || {};
        const merged = {
          shop_name: data.shop_name || "",
          address_1: data.address_1 || "",
          address_2: data.address_2 || "",
          address_3: data.address_3 || "",
          address_4: data.address_4 || "",
          header: data.header || "",
          tel_no: data.tel_no || "",
          tin: data.tin || "",
          note: data.note || "",
          footer_1: data.footer_1 || "",
          footer_2: data.footer_2 || "",
        };
        setTerminalInvoiceFormat(merged);
        InvoiceHelper.saveInvoiceFormatForTerminal(terminalId, merged, false);
        return merged;
      } catch (error) {
        console.error("Failed to load bill format in context:", error);
        return null;
      }
    },
    [terminalInvoiceFormat],
  );

  function applyTerminalSettingsStateAndStorage(res, hasFullKotValidity, setTerminalSettings) {
    const dashboards = [];
    if (res.is_order_dashboard_enabled) dashboards.push("ORDERS_DASHBOARD");
    if (res.is_table_dashboard_enabled) dashboards.push("TABLE_DASHBOARD");

    setTerminalSettings((prev) => ({
      ...prev,
      settingsCache: res,
      selectedDashboards: dashboards,
      loading: false,
      activeOrders: res.is_order_dashboard_enabled,
      tableDashboard: res.is_table_dashboard_enabled && hasFullKotValidity,
      displayAwaitingDispatchOrders: !!res?.config?.display_awaiting_dispatch_orders,
      showTokenNumber: !!res?.show_token_number,
      localTokenNumber: TerminalHelper.getLocalTokenNumber(),
      printOnPayment: res.print_invoice_on_payment_update,
    }));

    const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);
    TerminalHelper.saveTerminalSettings(TerminalHelper.buildTerminalSettingsMap(res, terminalId));
  }

  const fetchTerminalSettings = useCallback(
    async (force = false) => {
      if (terminalSetting.settingsCache && !force) {
        return terminalSetting.settingsCache;
      }
      const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);
      if (!terminalId) return;

      setTerminalSettings((prev) => ({ ...prev, loading: true }));
      try {
        const res = await TerminalService.getTerminal(terminalId);
        applyTerminalSettingsStateAndStorage(res, hasFullKotValidity, setTerminalSettings);
        return res;
      } catch (error) {
        toast.error("Failed to load terminal settings");
        setTerminalSettings((prev) => ({ ...prev, loading: false }));
      }
    },
    [hasFullKotValidity, terminalSetting.settingsCache],
  );

  const updateTerminalSettings = async (dashboards, systemView) => {
    const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);
    if (!terminalId) return;

    const payload = {
      is_order_dashboard_enabled: dashboards.includes("ORDERS_DASHBOARD"),
      is_table_dashboard_enabled: dashboards.includes("TABLE_DASHBOARD") && hasFullKotValidity,
      config: {
        ...(terminalSetting.settingsCache?.config || {}),
        erp_dashboard_enabled: systemView === SYSTEM_VIEW.ERP,
      },
    };

    setTerminalSettings((prev) => ({ ...prev, loading: true }));

    const updateContextAndLocal = () => {
      setTerminalSettings((prev) => ({
        ...prev,
        settingsCache: prev.settingsCache ? { ...prev.settingsCache, ...payload } : null,
        selectedDashboards: dashboards,
        loading: false,
        activeOrders: payload.is_order_dashboard_enabled,
        tableDashboard: payload.is_table_dashboard_enabled && hasFullKotValidity,
      }));

      TerminalHelper.saveTerminalSettings({
        systemType: systemView,
        isOrderDashboardEnabled: payload.is_order_dashboard_enabled,
        isTableDashboardEnabled: payload.is_table_dashboard_enabled && hasFullKotValidity,
      });

      eventsEmitter.emit(EVENTS.TERMINAL_SETTINGS_UPDATED);
    };

    try {
      await TerminalService.updateTerminalConfig(terminalId, payload);
      updateContextAndLocal();
      toast.success("Settings updated successfully");
      return true;
    } catch (error) {
      console.warn("Failed to sync terminal settings to server (offline):", error);
      updateContextAndLocal();
      toast.success("Settings saved locally (offline mode)");
      return true;
    }
  };

  const setLocalToken = (val) => {
    setTerminalSettings((prev) => {
      const nextToken = typeof val === "function" ? val(prev.localTokenNumber) : val;
      TerminalHelper.saveLocalTokenNumber(nextToken);
      return {
        ...prev,
        localTokenNumber: nextToken,
      };
    });
  };

  useEffect(() => {
    const savedSettings = TerminalHelper.getTerminalSettings() || {};
    setTerminalSettings((prev) => ({
      ...prev,
      tableDashboard: (savedSettings.isTableDashboardEnabled || false) && hasFullKotValidity,
      activeOrders: savedSettings.isOrderDashboardEnabled || false,
    }));
  }, [hasFullKotValidity]);

  useEffect(() => {
    const handleSettingsUpdate = () => {
      const savedSettings = TerminalHelper.getTerminalSettings() || {};
      const savedToken = TerminalHelper.getLocalTokenNumber();

      setTerminalSettings((prev) => ({
        ...prev,
        activeOrders: savedSettings.isOrderDashboardEnabled ?? prev.activeOrders,
        tableDashboard: (savedSettings.isTableDashboardEnabled ?? prev.tableDashboard) && hasFullKotValidity,
        displayAwaitingDispatchOrders:
          savedSettings.displayAwaitingDispatchOrders ?? prev.displayAwaitingDispatchOrders,
        showTokenNumber: savedSettings.showTokenNumber ?? prev.showTokenNumber,
        localTokenNumber: savedToken || prev.localTokenNumber,
        printOnPayment: savedSettings.printOnPayment ?? prev.printOnPayment,
        kotFormat: savedSettings.kotFormat ?? prev.kotFormat,
        consolidatedKotFormat: savedSettings.consolidatedKotFormat ?? prev.consolidatedKotFormat,
        barcodePrinterType: savedSettings.barcodePrinterType ?? prev.barcodePrinterType,
        barcodePrinterFormat: savedSettings.barcodePrinterFormat ?? prev.barcodePrinterFormat,
        barcodeCustomNote: savedSettings.barcodeCustomNote ?? prev.barcodeCustomNote,
        saleType: savedSettings.saleType ?? prev.saleType,
      }));
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.TERMINAL_SETTINGS_UPDATED, handleSettingsUpdate);
    return unsubscribeFn;
  }, [hasFullKotValidity]);

  return (
    <TerminalContext.Provider
      value={{
        ...terminalSetting,
        terminalInvoiceFormat,
        setTerminalInvoiceFormat,
        fetchTerminalSettings,
        updateTerminalSettings,
        fetchInvoiceFormat,
        setTerminalSettings,
        setLocalToken,
      }}
    >
      {children}
    </TerminalContext.Provider>
  );
};
